import React, { useState } from "react";
import { Box, Button, Typography, Skeleton, useTheme, useMediaQuery } from "@mui/material";

export default function Banner({ bannerData }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <div style={{ width: "100%" }}>
      <Box
        position="relative"
        width="100%"
        sx={{
          height: { xs: "80vh", sm: "100vh", md: "100vh" },
          overflow: "hidden",
        }}
      >
        {/* Skeleton Loader */}
        {!isLoaded && (
          <Skeleton
            animation="wave"
            variant="rectangular"
            width="100%"
            height="100%"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 2,
            }}
          />
        )}

        {/* Video or Image */}
        {(bannerData.videoUrl && !isMobile) ||
        (isMobile && !bannerData.mobileImageUrl && bannerData.videoUrl) ? (
          <Box
            component="video"
            key={isMobile && bannerData.mobileVideoUrl ? "mobile" : "desktop"}
            playsInline
            autoPlay
            loop
            muted
            preload="auto"
            onCanPlayThrough={() => setIsLoaded(true)}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
            }}
          >
            <source
              src={
                isMobile && bannerData.mobileVideoUrl
                  ? bannerData.mobileVideoUrl
                  : bannerData.videoUrl
              }
              type="video/mp4"
            />
          </Box>
        ) : (
          <Box
            component="img"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            onLoad={() => setIsLoaded(true)}
            src={
              isMobile && bannerData.mobileImageUrl
                ? bannerData.mobileImageUrl
                : bannerData.imageUrl
            }
            alt="Banner"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
            }}
          />
        )}

        {/* Overlay Content */}
        <div
          style={{
            position: "absolute",
            bottom: "5%",
            color: "white",
            paddingLeft: isMobile ? "16px" : isTablet ? "32px" : "64px",
            paddingRight: isMobile ? "16px" : isTablet ? "32px" : "64px",
            textAlign: isTablet ? "center" : "left",
            display: "flex",
            flexDirection: isTablet ? "column" : "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          {/* Left Section: Title + Price */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              justifyContent:
                bannerData.features.length > 0 && !isTablet
                  ? "flex-start"
                  : "center",
              flexWrap: "wrap",
              gap: "16px",
              flex: bannerData.features.length > 0 && !isTablet ? "0 0 50%" : "1",
              minWidth: 0,
            }}
          >
            {/* Title & Subtitle */}
            <Box
              sx={{
                borderRight: {
                  sm: bannerData.features.length > 0 && !isTablet && "1px solid white",
                },
                pr: { sm: !isTablet ? 3 : 0 },
                pb: { xs: 2, sm: 0 },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "KIAbodyLatinbasic_Bold",
                  lineHeight: 1.15,
                  fontSize: "clamp(24px, 4.5vw, 34px)",
                }}
              >
                {bannerData.title}
              </Typography>
              <Typography
                sx={{ fontSize: "clamp(13px, 1.8vw, 16px)", mt: 0.5 }}
              >
                {bannerData.subtitle}
              </Typography>
            </Box>

            {/* Price Section */}
            {bannerData.price && (
              <div>
                <Typography sx={{ fontSize: "clamp(13px, 1.6vw, 18px)" }}>
                  Price starts at
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "KIAbodyLatinbasic_Bold",
                    display: "block",
                    fontSize: "clamp(22px, 3.5vw, 30px)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {bannerData.price}
                </Typography>
              </div>
            )}
          </div>

          {/* Right Section: Features & Button */}
          {bannerData.features.length > 0 && (
            <div
              style={{
                flex: isTablet ? "1" : "0 0 50%",
                textAlign: "center",
                marginTop: isTablet ? "20px" : "0",
                width: "100%",
              }}
            >
              {/* Desktop Features */}
              <div
                style={{
                  display: isTablet ? "none" : "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "32px",
                  flexWrap: "wrap",
                }}
              >
                {bannerData.features.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "80px",
                    }}
                  >
                    <img
                      src={item.imgSrc}
                      alt="Feature Icon"
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: "50px",
                        marginBottom: "8px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "KIAbodyLatinbasic_Regular",
                        fontSize: "12px",
                      }}
                    >
                      {item.text}
                    </Typography>
                  </div>
                ))}
              </div>

              {/* Compact "Book Now" Button (mobile + tablet) */}
              {isTablet && (
                <Button
                  variant="contained"
                  sx={{
                    color: "#000",
                    backgroundColor: "#FFF",
                    px: 4,
                    py: 1,
                    mt: 2,
                    textTransform: "capitalize",
                    fontSize: "16px",
                    borderRadius: 0,
                    fontWeight: 500,
                    width: { xs: "70%", sm: "auto" },
                  }}
                >
                  Book Now
                </Button>
              )}
            </div>
          )}
        </div>
      </Box>
    </div>
  );
}
